import { Bar, BarChart, CartesianGrid, Legend, LabelList, ReferenceLine, ResponsiveContainer, Tooltip } from 'recharts';
import type { DeadlineMonth } from '../../../utils/panorama/panoramaDeadlinesByMonth';
import { toIsoDay } from '../../../utils/overviewDates';
import { CHART_COLORS } from '../vacationOverviewPage.definitions';
import { ChartTooltip } from './ChartTooltip';
import { EmptyChart } from './DashboardCard';
import { useCompactChart } from './useCompactChart';
import { CompactBarAxes, getCompactBarAxesConfig } from './compactBarAxes';

interface DeadlinesByMonthChartProps {
	data: DeadlineMonth[];
	referenceDate: Date;
	onMonthClick?: (year: number, monthNum: number, isPast: boolean) => void;
}

export function DeadlinesByMonthChart({ data, referenceDate, onMonthClick }: DeadlinesByMonthChartProps) {
	const { isCompact, rowHeight } = useCompactChart();
	const totalDeadlines = data.reduce((sum, m) => sum + m.expired + m.upcoming, 0);
	const todayIso = toIsoDay(referenceDate);

	if (totalDeadlines === 0) {
		return <EmptyChart message="Nenhum prazo vencido nem nos próximos meses." height={260} />;
	}

	const chartData = data.map(m => ({
		...m,
		monthLabel: isCompact ? m.shortLabel : m.month,
		sortKey: `${m.year}-${String(m.monthNum).padStart(2, '0')}`,
	}));

	const config = getCompactBarAxesConfig(isCompact);
	const currentMonthKey = todayIso.slice(0, 7);
	const currentMonthLabel = chartData.find(d => d.key === currentMonthKey)?.monthLabel ?? null;

	return (
		<ResponsiveContainer width="100%" height={rowHeight(data.length) || 260}>
			<BarChart data={chartData} layout={config.layout} margin={config.margin}>
				<CartesianGrid stroke={CHART_COLORS.grid} vertical={!isCompact} />
				<CompactBarAxes isCompact={isCompact} categoryKey="monthLabel" />
				<Tooltip content={<ChartTooltip unit=" prazo(s)" />} />
				<Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
				{currentMonthLabel && (isCompact ? (
					<ReferenceLine
						y={currentMonthLabel}
						stroke={CHART_COLORS.axis}
						strokeDasharray="4 4"
						label={{ value: 'hoje', position: 'right', fontSize: 10, fill: CHART_COLORS.axis }}
					/>
				) : (
					<ReferenceLine
						x={currentMonthLabel}
						stroke={CHART_COLORS.axis}
						strokeDasharray="4 4"
						label={{ value: 'hoje', position: 'top', fontSize: 10, fill: CHART_COLORS.axis }}
					/>
				))}
				<Bar
					dataKey="expired"
					name="Já vencidas"
					fill={CHART_COLORS.alert}
					onClick={barData => onMonthClick?.(barData.payload.year, barData.payload.monthNum, barData.payload.isPast)}
					cursor={onMonthClick ? 'pointer' : 'default'}
					radius={config.barRadius}
				>
					{isCompact && <LabelList dataKey="expired" position="right" fontSize={11} />}
				</Bar>
				<Bar
					dataKey="upcoming"
					name="A vencer"
					fill={CHART_COLORS.bar}
					onClick={barData => onMonthClick?.(barData.payload.year, barData.payload.monthNum, barData.payload.isPast)}
					cursor={onMonthClick ? 'pointer' : 'default'}
					radius={config.barRadius}
				>
					{isCompact && <LabelList dataKey="upcoming" position="right" fontSize={11} />}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
}
